import React, { useState } from 'react';
import { Shield, Lock, ArrowRight, AlertCircle, HeartPulse } from 'lucide-react';
import { motion } from 'framer-motion';

interface VisitorPinGateProps {
    handle: string;
    hospitalName: string;
    logoUrl?: string | null;
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    onVerified: (token: string) => void;
    mode: 'pin' | 'private';
}

const VisitorPinGate: React.FC<VisitorPinGateProps> = ({
    handle, hospitalName, logoUrl, primaryColor, secondaryColor, backgroundColor, onVerified, mode
}) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!pin.trim()) return;
        setLoading(true);
        setError('');

        try {
            const res = await fetch(`/api/hospitals/verify-pin/${handle}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin: pin.trim() }),
            });
            const data = await res.json();

            if (res.ok && data.token) {
                sessionStorage.setItem(`visitor_token_${handle}`, data.token);
                onVerified(data.token);
            } else if (res.status === 429) {
                setError('Demasiados intentos. Intenta de nuevo en 1 hora.');
            } else {
                setError('PIN incorrecto. Verifica con recepción.');
                setPin('');
            }
        } catch {
            setError('Error de conexión. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    // Private mode — no access at all
    if (mode === 'private') {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-md w-full text-center"
                >
                    <div className="w-20 h-20 rounded-3xl mx-auto mb-8 flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: `${primaryColor}15`, border: `2px solid ${primaryColor}20` }}>
                        <Lock className="w-10 h-10" style={{ color: primaryColor }} />
                    </div>
                    <h1 className="text-2xl font-black mb-3 tracking-tight" style={{ color: secondaryColor }}>
                        Acceso Privado
                    </h1>
                    <p className="text-slate-500 mb-8 leading-relaxed">
                        Este hospital utiliza acceso privado para familiares. Si recibiste un código de seguimiento, búscalo directamente:
                    </p>
                    <a
                        href="/family"
                        className="inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-full shadow-lg transition-all hover:brightness-110"
                        style={{ backgroundColor: primaryColor }}
                    >
                        <HeartPulse className="w-5 h-5" />
                        Buscar mi paciente
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </motion.div>
            </div>
        );
    }

    // PIN mode
    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor }}>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md w-full"
            >
                {/* Hospital Brand */}
                <div className="text-center mb-10">
                    {logoUrl ? (
                        <img src={logoUrl} alt={hospitalName} className="h-16 mx-auto mb-6 object-contain" />
                    ) : (
                        <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-sm"
                            style={{ backgroundColor: `${primaryColor}10`, border: `1px solid ${primaryColor}20` }}>
                            <HeartPulse className="w-8 h-8" style={{ color: primaryColor }} />
                        </div>
                    )}
                    <h1 className="text-2xl font-black tracking-tight mb-2" style={{ color: secondaryColor }}>
                        {hospitalName}
                    </h1>
                    <p className="text-slate-400 text-sm font-medium">
                        Tablero de Cirugías — Recepción Digital
                    </p>
                </div>

                {/* PIN Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                    <div className="p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                                <Shield className="w-5 h-5" style={{ color: primaryColor }} />
                            </div>
                            <div>
                                <h2 className="text-lg font-black tracking-tight" style={{ color: secondaryColor }}>
                                    Acceso Protegido
                                </h2>
                                <p className="text-[11px] text-slate-400 font-medium">
                                    Ingresa el PIN que te proporcionaron en recepción
                                </p>
                            </div>
                        </div>

                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            value={pin}
                            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
                            placeholder="• • • •"
                            autoFocus
                            className="w-full text-center text-4xl font-mono font-black tracking-[0.3em] py-5 rounded-2xl border-2 border-slate-100 focus:border-indigo-400 outline-none transition-all bg-slate-50 focus:bg-white placeholder:text-slate-200"
                            style={{ color: secondaryColor }}
                        />

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-2 text-red-500 text-sm font-medium mt-4 bg-red-50 px-4 py-3 rounded-xl"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={pin.length < 4 || loading}
                        className="w-full py-5 text-white font-black text-[11px] uppercase tracking-[0.2em] transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 flex items-center justify-center gap-2"
                        style={{ backgroundColor: pin.length >= 4 ? primaryColor : '#cbd5e1' }}
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <Lock className="w-4 h-4" />
                                Verificar Acceso
                            </>
                        )}
                    </button>
                </form>

                {/* Alternative access */}
                <div className="text-center mt-8">
                    <p className="text-xs text-slate-400 mb-2">¿Ya tienes un código de paciente?</p>
                    <a
                        href="/family"
                        className="text-sm font-bold hover:underline transition-colors flex items-center justify-center gap-1"
                        style={{ color: primaryColor }}
                    >
                        Buscar paciente directamente <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                </div>
            </motion.div>
        </div>
    );
};

export default VisitorPinGate;
